<core-generic-modal
    [isOpen]="isOpen()"
    [title]="'bookings.import.title'"
    [mode]="modalMode"
    [customTemplate]="importBody"
    [buttonConfig]="footerButtons()"
    (close)="onClose()">
</core-generic-modal>

<ng-template #importBody>
    <div class="bkimp-panel__body">

        <p class="bkimp-hint">{{ 'bookings.import.hint' | translate }}</p>

        <button class="c-btn c-btn--outline c-btn--sm" type="button" (click)="downloadTemplate()">
            <i class="fa-solid fa-download"></i>
            {{ 'bookings.import.downloadTemplate' | translate }}
        </button>

        <input #fileInput type="file" accept=".csv,.xls,.xlsx" class="bkimp-file-input" (change)="onFileSelected($event)" />

        <!-- Dropzone (idle) -->
        @if (status() === 'idle') {
            <div class="bkimp-dropzone" [class.is-dragover]="isDragOver()"
                (click)="openFilePicker()"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave()"
                (drop)="onDrop($event)">
                <i class="fa-solid fa-file-arrow-up"></i>
                <p>{{ 'bookings.import.dropzoneText' | translate }}</p>
                <span class="bkimp-dropzone__hint">.csv, .xls, .xlsx</span>
            </div>
        }

        <!-- Selected file -->
        @if (status() !== 'idle' && status() !== 'success') {
            <div class="bkimp-file-row">
                <i class="fa-regular fa-file-excel"></i>
                <div class="bkimp-file-row__info">
                    <span class="bkimp-file-row__name">{{ selectedFile()?.name }}</span>
                    <span class="bkimp-file-row__size">{{ formatFileSize(selectedFile()?.size ?? 0) }}</span>
                </div>
                <button class="c-btn c-btn--stroke c-btn--sm" type="button"
                    [disabled]="status() === 'uploading'"
                    (click)="openFilePicker()">
                    {{ 'bookings.import.replaceFile' | translate }}
                </button>
            </div>
        }

        <!-- Success -->
        @if (status() === 'success' && importResult(); as result) {
            <div class="c-alert context:success bkimp-summary">
                <i class="fa-solid fa-circle-check"></i>
                <div>
                    <strong>{{ 'bookings.import.successTitle' | translate }}</strong>
                    <p>
                        {{ 'bookings.import.summaryLine' | translate: {
                            bookingsCreated: result.bookingsCreated,
                            bookingsMatched: result.bookingsMatched,
                            itemsCreated: result.itemsCreated,
                            itemsSkipped: result.itemsSkipped
                        } }}
                    </p>
                </div>
            </div>

            @if (result.warnings.length > 0) {
                <div class="bkimp-issues">
                    <p class="bkimp-issues__title">{{ 'bookings.import.warningsTitle' | translate }}</p>
                    <div class="bkimp-issues__list">
                        @for (warning of result.warnings; track $index) {
                            <div class="bkimp-issue-card bkimp-issue-card--warning">
                                <div class="bkimp-issue-card__header">
                                    <span class="c-tag context:warning">warning</span>
                                    <span class="bkimp-issue-kv">
                                        <span class="bkimp-issue-kv__label">{{ 'bookings.import.col.booking' | translate }}</span>
                                        <span class="bkimp-issue-kv__value">{{ warning.bookingNumber ?? '—' }}</span>
                                    </span>
                                    <span class="bkimp-issue-kv">
                                        <span class="bkimp-issue-kv__label">{{ 'bookings.import.col.item' | translate }}</span>
                                        <span class="bkimp-issue-kv__value">{{ warning.bookingItem ?? '—' }}</span>
                                    </span>
                                    <code class="bkimp-issue-cell">{{ cellLabel(warning) }}</code>
                                </div>
                                <p class="bkimp-issue-card__message">{{ warning.message }}</p>
                            </div>
                        }
                    </div>
                </div>
            }
        }

        <!-- Validation error (422) -->
        @if (status() === 'validation_error' && validationResponse(); as response) {
            <div class="c-alert context:error bkimp-summary">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <div>
                    <strong>{{ 'bookings.import.validationErrorTitle' | translate }}</strong>
                    <p>
                        {{ 'bookings.import.validationSummaryLine' | translate: {
                            errorCount: response.summary.errorCount,
                            rowsWithErrors: response.summary.rowsWithErrors,
                            bookingsAffected: response.summary.bookingsAffected
                        } }}
                    </p>
                </div>
            </div>

            <div class="bkimp-issues">
                <div class="bkimp-issues__list">
                    @for (issue of response.errors; track $index) {
                        <div class="bkimp-issue-card" [class.bkimp-issue-card--warning]="issue.severity === 'warning'">
                            <div class="bkimp-issue-card__header">
                                <span class="c-tag context:{{ issue.severity === 'error' ? 'error' : 'warning' }}">
                                    {{ issue.severity }}
                                </span>
                                <span class="bkimp-issue-kv">
                                    <span class="bkimp-issue-kv__label">{{ 'bookings.import.col.booking' | translate }}</span>
                                    <span class="bkimp-issue-kv__value">{{ issue.bookingNumber ?? '—' }}</span>
                                </span>
                                <span class="bkimp-issue-kv">
                                    <span class="bkimp-issue-kv__label">{{ 'bookings.import.col.item' | translate }}</span>
                                    <span class="bkimp-issue-kv__value">{{ issue.bookingItem ?? '—' }}</span>
                                </span>
                                <code class="bkimp-issue-cell">{{ cellLabel(issue) }}</code>
                                <span class="bkimp-issue-kv">
                                    <span class="bkimp-issue-kv__label">{{ 'bookings.import.col.field' | translate }}</span>
                                    <span class="bkimp-issue-kv__value">{{ issue.header ?? '—' }}</span>
                                </span>
                            </div>
                            <p class="bkimp-issue-card__message">{{ issue.message }}</p>
                        </div>
                    }
                </div>
            </div>
        }

        <!-- Generic request error (400 / 401) -->
        @if (status() === 'request_error') {
            <div class="c-alert context:error bkimp-summary">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span>{{ requestErrorMessage() }}</span>
            </div>
        }

    </div>
</ng-template>
